import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * 統一認証サービス
 * 全てのAPIで共通の認証処理を提供
 */
class AuthService {
  private static instance: AuthService;
  private tokenCache: {
    token: string | null;
    expiry: number | null;
  } = {
    token: null,
    expiry: null,
  };

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * 統一認証ヘッダー取得
   * API Gateway Cognito Authorizer用にID Tokenを使用
   */
  async getAuthHeaders(): Promise<HeadersInit> {
    try {
      const token = await this.getValidToken();
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      return headers;
    } catch (error) {
      console.error('Authentication failed:', error);
      this.handleAuthError(error);
      throw new Error('認証が必要です');
    }
  }

  /**
   * 有効なトークンを取得（キャッシュ機能付き）
   */
  private async getValidToken(): Promise<string> {
    // キャッシュされたトークンが有効かチェック
    if (this.tokenCache.token && this.tokenCache.expiry && Date.now() < this.tokenCache.expiry) {
      return this.tokenCache.token;
    }

    // 新しいトークンを取得
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    
    if (!idToken) {
      throw new Error('No authentication token available');
    }

    const tokenString = idToken.toString();
    const tokenParts = tokenString.split('.');
    
    // JWTの形式チェック
    if (tokenParts.length !== 3) {
      throw new Error('Invalid token format');
    }

    // トークンの有効期限を解析（簡易版）
    try {
      const payload = JSON.parse(atob(tokenParts[1]));
      const expiry = payload.exp * 1000; // Unix timestamp to milliseconds
      
      // キャッシュに保存（有効期限の5分前まで）
      this.tokenCache = {
        token: tokenString,
        expiry: expiry - (5 * 60 * 1000), // 5分前
      };
    } catch (error) {
      // トークン解析失敗時はキャッシュしない
      console.warn('Failed to parse token expiry:', error);
    }

    return tokenString;
  }

  /**
   * トークンキャッシュをクリア
   */
  clearTokenCache(): void {
    this.tokenCache = {
      token: null,
      expiry: null,
    };
  }

  /**
   * 統一エラーハンドリング
   */
  handleAuthError(error: any): never {
    // 認証エラー（401）と権限エラー（403）を区別
    if (error.name === 'ForbiddenException') {
      // 403 Forbidden: 認証は通っているが権限不足
      console.error('Access denied: Insufficient permissions', error.message);
      throw new Error('アクセス権限がありません。管理者にお問い合わせください。');
    }

    // 認証エラーの場合のみ認証状態をクリア
    if (error.name === 'NotAuthorizedException' || 
        error.name === 'UnauthorizedException' ||
        error.message?.includes('Unauthorized') ||
        error.message?.includes('Authentication required')) {
      
      // トークンキャッシュをクリア
      this.clearTokenCache();
      
      // ログイン画面にリダイレクト
      console.log('Authentication error detected, clearing auth state and redirecting to login...');
      
      // Amplify認証状態を強制的にクリア
      this.forceSignOut().catch(err => {
        console.warn('Failed to force sign out:', err);
      });
      
      // 現在のURLを保存してログイン後に復帰
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      
      // ログイン画面への遷移
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    throw error;
  }

  /**
   * 強制的な認証状態クリア（エラー時用）
   */
  private async forceSignOut(): Promise<void> {
    try {
      const { signOut } = await import('aws-amplify/auth');
      await signOut({ global: true });
    } catch (error) {
      console.warn('Force sign out failed, clearing local storage:', error);
    } finally {
      // 常にローカルストレージをクリア
      this.clearAllStorages();
    }
  }

  /**
   * 全ストレージクリア（認証関連）
   */
  private clearAllStorages(): void {
    try {
      // Amplifyのローカルストレージをクリア
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('amplify-') || 
            key.startsWith('CognitoIdentityServiceProvider') ||
            key.startsWith('aws-amplify-') ||
            key.includes('cognito')) {
          localStorage.removeItem(key);
        }
      });
      
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('amplify-') || 
            key.startsWith('CognitoIdentityServiceProvider') ||
            key.startsWith('aws-amplify-') ||
            key.includes('cognito')) {
          sessionStorage.removeItem(key);
        }
      });
      
      console.log('Auth storages cleared');
    } catch (error) {
      console.warn('Failed to clear storages:', error);
    }
  }

  /**
   * 認証状態を完全にリセット
   */
  async resetAuthState(): Promise<void> {
    try {
      // トークンキャッシュをクリア
      this.clearTokenCache();
      
      // Amplify認証状態をクリア
      const { signOut } = await import('aws-amplify/auth');
      await signOut({ global: true });
    } catch (error) {
      console.warn('Failed to sign out:', error);
    } finally {
      // 常にストレージをクリア
      this.clearAllStorages();
    }
  }

  /**
   * 現在の認証状態を確認
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      console.log('🔄 AuthService - Checking if authenticated...');
      const token = await this.getValidToken();
      console.log('✅ AuthService - Token obtained, user is authenticated');
      return true;
    } catch (error) {
      console.log('❌ AuthService - No valid token, user not authenticated:', error);
      return false;
    }
  }

  /**
   * ユーザー情報を取得
   */
  async getCurrentUser(): Promise<{
    id: string;
    email: string;
    name: string;
    role: string;
    groups: string[];
  } | null> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        return null;
      }

      const tokenString = idToken.toString();
      const tokenParts = tokenString.split('.');
      
      if (tokenParts.length !== 3) {
        return null;
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const cognitoGroups = payload['cognito:groups'] ? 
        (Array.isArray(payload['cognito:groups']) ? payload['cognito:groups'] : payload['cognito:groups'].split(',')) : 
        [];
      
      return {
        id: payload.sub,
        email: payload.email,
        name: payload.name || payload.email,
        role: cognitoGroups.includes('admin') ? 'admin' : 
              cognitoGroups.includes('manager') ? 'manager' : 'user',
        groups: cognitoGroups,
      };
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * ログアウト処理（IdPサインアウトフロー対応）
   */
  async logout(): Promise<void> {
    try {
      console.log('🔄 Starting logout process with IdP signout flow...');
      
      // トークンキャッシュをクリア
      this.clearTokenCache();
      
      // Amplify Auth を使用してログアウト（IdPサインアウトフロー）
      const { signOut } = await import('aws-amplify/auth');
      await signOut({ global: true });
      
      console.log('✅ Amplify signOut completed');
      
      // ストレージをクリア
      this.clearAllStorages();
      
      // Cognito Hosted UIでのログアウトURL構築
      const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || 'enterprise-auth-dev-auth';
      const region = import.meta.env.VITE_AWS_REGION || 'ap-northeast-1';
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '6q5ln14653g4196ffi7jop1cel';
      const logoutUri = encodeURIComponent(window.location.origin);
      
      const hostedUILogoutUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
      
      console.log('🔄 Redirecting to Cognito Hosted UI logout:', hostedUILogoutUrl);
      
      // Cognito Hosted UIのログアウトページにリダイレクト
      window.location.href = hostedUILogoutUrl;
      
    } catch (error) {
      console.error('💥 Logout failed:', error);
      
      // エラーが発生した場合でもローカル状態をクリア
      this.clearTokenCache();
      this.clearAllStorages();
      
      // 手動でログイン画面にリダイレクト
      window.location.href = '/login';
    }
  }

  /**
   * 緊急ログアウト処理（ローカルのみ）
   */
  async emergencyLogout(): Promise<void> {
    try {
      console.log('🚨 Emergency logout - clearing local state only');
      
      // トークンキャッシュをクリア
      this.clearTokenCache();
      
      // ローカルストレージをクリア
      this.clearAllStorages();
      
      // Amplifyの状態をクリア（エラーを無視）
      try {
        const { signOut } = await import('aws-amplify/auth');
        await signOut({ global: true });
      } catch (amplifyError) {
        console.warn('Amplify signOut failed during emergency logout:', amplifyError);
      }
      
      // ログイン画面にリダイレクト
      window.location.href = '/login';
      
    } catch (error) {
      console.error('💥 Emergency logout failed:', error);
      // 最後の手段として強制リダイレクト
      window.location.href = '/login';
    }
  }

  /**
   * デバッグ情報を取得
   */
  async getDebugInfo(): Promise<{
    isAuthenticated: boolean;
    tokenCached: boolean;
    tokenExpiry: Date | null;
    user: any;
  }> {
    const isAuthenticated = await this.isAuthenticated();
    const user = await this.getCurrentUser();
    
    return {
      isAuthenticated,
      tokenCached: !!this.tokenCache.token,
      tokenExpiry: this.tokenCache.expiry ? new Date(this.tokenCache.expiry) : null,
      user,
    };
  }
}

// シングルトンインスタンスをエクスポート
export const authService = AuthService.getInstance();
export default authService;
